/**
 * #997 — admin offer banner helpers (browser canvas, no dependencies). Mirrors the partner portal's
 * `components/offers/bannerImage.ts` (the portals share no code): same types, size and 2:1 crop.
 * Kept apart from the component so tests can stub image decoding (jsdom has no decoder).
 */

export const BANNER_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const BANNER_MAX_BYTES = 5 * 1024 * 1024;
export const BANNER_MIN_WIDTH = 1200;
export const BANNER_MIN_HEIGHT = 600;
/** Output is capped so a 6000-px photo does not turn into a multi-MB upload. */
export const BANNER_OUTPUT_MAX_WIDTH = 2400;
export const BANNER_JPEG_QUALITY = 0.85;

export function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('IMAGE_DECODE_FAILED'));
    };
    img.src = url;
  });
}

export async function readImageDimensions(file: Blob): Promise<{ width: number; height: number }> {
  const img = await loadImage(file);
  return { width: img.naturalWidth, height: img.naturalHeight };
}

/** Largest centred 2:1 rectangle inside a width×height image. */
export function centreCrop2to1(width: number, height: number): { sx: number; sy: number; sw: number; sh: number } {
  const sw = Math.min(width, height * 2);
  const sh = sw / 2;
  return { sx: Math.round((width - sw) / 2), sy: Math.round((height - sh) / 2), sw: Math.round(sw), sh: Math.round(sh) };
}

/** True when the centred 2:1 crop is at least 1200×600. */
export function meetsMinimumSize(width: number, height: number): boolean {
  const { sw, sh } = centreCrop2to1(width, height);
  return sw >= BANNER_MIN_WIDTH && sh >= BANNER_MIN_HEIGHT;
}

/** Centre-crops to 2:1 on a canvas and exports a JPEG File (~0.85 quality). */
export async function cropToBanner(file: File): Promise<File> {
  const img = await loadImage(file);
  const { sx, sy, sw, sh } = centreCrop2to1(img.naturalWidth, img.naturalHeight);
  const outW = Math.min(sw, BANNER_OUTPUT_MAX_WIDTH);
  const outH = Math.round(outW / 2);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('CANVAS_UNAVAILABLE');
  // JPEG has no alpha: paint white under transparent PNG/WebP pixels.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', BANNER_JPEG_QUALITY));
  if (!blob) throw new Error('CANVAS_EXPORT_FAILED');
  const base = file.name.replace(/\.[^.]+$/, '') || 'banner';
  return new File([blob], `${base}-2x1.jpg`, { type: 'image/jpeg' });
}

/** Pre-upload checks that need no decoding. Returns the message to show, or null. */
export function checkBannerFile(file: File): string | null {
  if (!BANNER_ACCEPTED_TYPES.includes(file.type)) return 'Use a JPG, PNG or WebP image.';
  if (file.size > BANNER_MAX_BYTES) return 'The image is larger than 5 MB.';
  return null;
}
